import { ChevronDownIcon } from '@systemeio/ui/icons'
import { useState } from 'react'
import {
  MenuItemType,
  SingleItemDropdownInterface,
  SingleItemHrefInterface,
} from 'shared/components/header/types/menu-interface'
import { checkNestedIsActive } from 'shared/components/header/utils/check-nested-is-active'
import { checkNestedRendered } from 'shared/components/header/utils/check-nested-rendered'
import { isFlatMenuItem } from 'shared/components/header/utils/is-flat-menu-item'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'

const SideMenuItem = ({ item }: { item: MenuItemType }) =>
  isFlatMenuItem(item) ? <SideMenuItemFlat {...item} /> : <SideMenuDropdown {...item} />

export default SideMenuItem

export function SideMenuItemFlat(item: SingleItemHrefInterface) {
  if (!item.shouldRender) return null
  const isActive = item.isActive(item.href)

  return (
    <div className="group relative inline-block transition-all">
      <div
        className={`flex items-center gap-4 ${
          isActive && '[&>svg]:fill-brand-primary'
        } [&>svg]:w-[24px]`}
      >
        {item.mobileIconRenderer && item.mobileIconRenderer()}
        <LinkWithoutPrefetch
          className={`${
            isActive ? 'text-brand-primary' : 'text-text-primary'
          } inline-flex grow items-center text-lg font-bold tracking-wide`}
          href={item.href}
          target={item.target}
        >
          {item.name}
        </LinkWithoutPrefetch>
      </div>
    </div>
  )
}

function SideMenuDropdown(item: SingleItemDropdownInterface) {
  const isActive = checkNestedIsActive(item)
  const [open, setOpen] = useState(false)
  const dropdownItems = item.dropdownItems
  if (!checkNestedRendered(item.dropdownItems)) return null
  return (
    <div>
      <div
        className={`flex cursor-pointer items-center gap-4 ${
          isActive && '[&>svg]:fill-brand-primary'
        } [&>svg]:w-[24px]`}
        onClick={() => setOpen(prev => !prev)}
      >
        {item.mobileIconRenderer && item.mobileIconRenderer()}
        <div
          className={`${
            isActive ? 'text-brand-primary' : 'text-text-primary'
          } inline-flex grow items-center text-lg font-bold tracking-wide`}
        >
          {item.name}
          <ChevronDownIcon
            className={`ml-auto self-center transition-transform ${open && 'rotate-180'}`}
            width={15}
            height={5}
          />
        </div>
      </div>
      {open && <SideMenuDropdownPanel dropdownItems={dropdownItems} />}
    </div>
  )
}

const SideMenuDropdownPanel = ({
  dropdownItems,
}: Pick<SingleItemDropdownInterface, 'dropdownItems'>) => {
  return (
    <div className="flex flex-col gap-2 whitespace-nowrap pl-10 pt-2 text-sm">
      {dropdownItems.map(item => {
        if (isFlatMenuItem(item)) {
          return <SideMenuSubItem key={item.name} {...item} />
        } else {
          return <SideSubMenu key={item.name} {...item} />
        }
      })}
    </div>
  )
}

const SideSubMenu = (item: SingleItemDropdownInterface) => {
  const [open, setOpen] = useState(false)
  const isActive = checkNestedIsActive(item)
  const dropdownItems = item.dropdownItems
  if (!checkNestedRendered(item.dropdownItems)) return null
  return (
    <div>
      <div
        className={`flex cursor-pointer items-center gap-4 ${
          isActive && '[&>svg]:fill-brand-primary'
        } [&>svg]:w-[24px]`}
        onClick={() => setOpen(prev => !prev)}
      >
        {item.mobileIconRenderer && item.mobileIconRenderer()}
        <div className={getSubItemClass(isActive)}>
          {item.name}
          <ChevronDownIcon
            className={`ml-auto self-center transition-transform ${open && 'rotate-180'}`}
            width={15}
            height={5}
          />
        </div>
      </div>
      {open && <SideMenuDropdownPanel dropdownItems={dropdownItems} />}
    </div>
  )
}

function SideMenuSubItem(item: SingleItemHrefInterface) {
  if (!item.shouldRender) return null
  const isActive = item.isActive(item.href)
  return (
    <LinkWithoutPrefetch className={getSubItemClass(isActive)} href={item.href}>
      {item.name}
    </LinkWithoutPrefetch>
  )
}

const getSubItemClass = (isActive: boolean) =>
  `${
    isActive ? 'text-brand-primary' : 'text-text-primary'
  } mb-1 hover:text-brand-primary text-[15px] font-light flex items-center justify-between w-full main-transition-colors`
